// The application snapshot rendered as review fields.
//
// Lives apart from any one page: the full-page review workspace and the list
// drawer both need it, and it is ~500 lines of field markup that must not be
// duplicated into a second, silently diverging copy.

import { Box, Typography } from '@mui/material';
import { formatBusinessDuration } from '@norvix/onboarding';
import { useContext } from 'react';

import {
  DocumentItem,
  Field,
  ReviewContext,
  asArray,
  asRecord,
  asString,
  asStringArray,
  collectDocuments,
  joinAddress,
  type DocRef,
} from './shared.js';
import { APP_LOCALE } from '../../../utils/locale.js';


/** Renders the PII-light draft snapshot the customer flow synced. */
export function SnapshotView({
  snapshot,
  applicationId,
  showPeople = true,
  showDocuments = true,
}: {
  snapshot: Record<string, unknown>;
  applicationId: string;
  /**
   * Off inside the review workspace: the people live on their own tab beside
   * their identity checks, and the documents on theirs beside a viewer. The
   * same list rendered twice on one page is not thoroughness — it leaves a
   * reviewer unsure which copy is authoritative.
   */
  showPeople?: boolean;
  showDocuments?: boolean;
}): JSX.Element {
  const contact = asRecord(snapshot['contact']);
  const isBusinessApp = asString(snapshot['accountType']) === 'BUSINESS';
  const details = asRecord(snapshot['details']);
  const address = asRecord(details['address']);
  const identity = asRecord(snapshot['identity']);
  const business = asRecord(snapshot['business']);
  const company = asRecord(business['company']);
  // One row per human with every role they hold, rather than the same person
  // listed twice under two headings. Applications captured before the single
  // list are folded in so they stay reviewable.
  const people =
    asArray(business['people']).length > 0
      ? asArray(business['people'])
      : [
          ...asArray(business['directors']).map((d) => ({
            ...asRecord(d),
            positions: [asString(asRecord(d)['role']) || 'DIRECTOR'],
          })),
          ...asArray(business['ubos']).map((u) => ({ ...asRecord(u), isUbo: true })),
        ];
  const partners = asArray(business['partners']);
  const bizActivity = asRecord(business['activity']);
  const bizDeclaration = asRecord(business['declaration']);
  const bizFinancial = asRecord(business['financial']);
  const consents = asRecord(snapshot['consents']);
  const grantedConsents = Object.entries(consents).filter(([, granted]) => granted === true);
  const documents = collectDocuments(snapshot);

  // The vault, not the snapshot, decides what must be reviewed — and the two
  // disagree: a file uploaded and then replaced stays in the vault while the
  // snapshot forgets it. Those rows were invisible here yet still blocked the
  // approval, so a reviewer could clear every document on screen and remain
  // stuck. Append them, labelled for what they are.
  const review = useContext(ReviewContext);
  const referenced = new Set(documents.map((doc) => doc.id).filter((id) => id !== ''));
  const unreferenced: DocRef[] = [...review.reviews.values()]
    .filter((stored) => !referenced.has(stored.id))
    .map((stored) => ({
      id: stored.id,
      label: stored.source === 'STAFF_UPLOAD' ? 'Filed by an agent'
        : stored.source === 'PROVIDER_CHECK' ? 'Captured by the identity check'
        : 'Uploaded earlier — superseded, not referenced by the current application',
      name: stored.filename,
      sizeLabel: `${String(Math.max(Math.round(stored.sizeBytes / 1024), 1))} KB`,
      status: stored.status,
    }));
  const allDocuments = [...documents, ...unreferenced];

  const isBusiness = asString(snapshot['accountType']) === 'BUSINESS';
  const addressLine = Object.values(address)
    .map((part) => asString(part))
    .filter((part) => part.length > 0)
    .join(', ');

  return (
    <>
      <Typography variant="subtitle2" gutterBottom>
        Contact
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Field
          label="Email"
          value={`${asString(contact['email'])}${contact['emailVerified'] === true ? ' ✓' : ''}`}
        />
        <Field
          label="Mobile"
          value={`${asString(contact['phone'])}${contact['phoneVerified'] === true ? ' ✓' : ''}`}
        />
      </Box>

      {/* A company has no personal-details or self-identity block — the humans
          are under People, verified individually. Rendering the personal
          sections here as rows of em dashes made a business application look
          half-empty and made reviewers hunt for data that lives elsewhere. */}
      {isBusinessApp ? null : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Personal details
          </Typography>
          <Box sx={{ mb: 2 }}>
            {/* First and last separately, each on its own snapshot path.
                They used to be one joined "Name" row, and a composed row has no
                path — so Field rendered it read-only and pressing Edit changed
                nothing at all on this tab. A reviewer could see a typo in a
                name and had no way to correct it, which is how "Mihael;" got as
                far as the identity provider. */}
            <Field
              label="First name"
              value={asString(details['firstName'])}
              path="details.firstName"
            />
            <Field
              label="Last name"
              value={asString(details['lastName'])}
              path="details.lastName"
            />
            <Field
              label="Date of birth"
              value={asString(details['dateOfBirth'])}
              path="details.dateOfBirth"
            />
            <Field
              label="Country of residence"
              value={asString(details['countryOfResidence']).toUpperCase()}
              path="details.countryOfResidence"
            />
            {/* Editable, and blank on every application taken before the form
                asked for it. That is how it should read: nobody has stated this
                person's nationality, so nothing should imply one. An account
                provider that requires it will name it, and it gets filled in
                here — from a document, not from where they happen to live. */}
            <Field
              label="Country of citizenship"
              value={asString(details['citizenship']).toUpperCase()}
              path="details.citizenship"
            />
            {/* The joined line stays read-only — it is several fields printed
                together, so there is nothing single to write back. The parts
                below it are the editable ones. */}
            <Field label="Address" value={addressLine} />
            {/* The reverse-solicitation declaration, shown wherever one was
                made. A Canadian MSB may serve an EU or EEA applicant only where
                they approached it themselves, and it is the firm that must be
                able to evidence it — so a reviewer sees the applicant's own
                words, not a tick. */}
            {(() => {
              const rs = asRecord(details['reverseSolicitation']);
              if (rs['confirmed'] !== true) return null;
              return (
                <>
                  <Field
                    label="Reverse solicitation"
                    value={`Declared${
                      asString(rs['declaredAt']) === ''
                        ? ''
                        : ` ${new Date(asString(rs['declaredAt'])).toLocaleString(APP_LOCALE)}`
                    }${asString(rs['country']) === '' ? '' : ` · ${asString(rs['country'])}`}`}
                  />
                  <Field label="In their own words" value={asString(rs['statement'])} />
                </>
              );
            })()}
            {Object.entries(address).map(([key, value]) => (
              <Field
                key={key}
                label={`Address · ${key.replace(/([A-Z])/g, ' $1').toLowerCase().trim()}`}
                value={asString(value)}
                path={`details.address.${key}`}
              />
            ))}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Identity
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Field label="Method" value={asString(identity['method'])} />
            <Field label="Verified" value={identity['verified'] === true ? 'Yes' : 'No'} />
            <Field
              label="Consents granted"
              value={grantedConsents.length === 0 ? 'None' : grantedConsents.map(([id]) => id).join(', ')}
            />
          </Box>
        </>
      )}

      {isBusiness ? (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Company
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Field label="Legal name" value={asString(company['legalName'])} path="business.company.legalName" />
            <Field label="Registration #" value={asString(company['registrationNumber'])} path="business.company.registrationNumber" />
            <Field label="Country of registration" value={asString(company['countryOfRegistration']).toUpperCase()} />
            {/* The provider's own classification, chosen by the applicant. This
                read company.industry — a six-option dropdown removed as a
                duplicate — so it would have been blank on every application. */}
            {/* Both levels when both were chosen — a reviewer asked "no sub
                activity?" when only the fallback rendered. */}
            <Field
              label="Business activity"
              value={[
                asString(bizActivity['baseActivityLabel']),
                asString(bizActivity['subActivityLabel']),
              ]
                .filter((part) => part !== '')
                .join(' — ')}
            />
            {/* Declared per person now; a single company-level answer could not
                say WHICH owner, which is what reporting needs. */}
            <Field
              label="US persons declared"
              value={
                people
                  .map((entry) => asRecord(entry))
                  .filter((entry) => entry['isUsPerson'] === true)
                  .map((entry) =>
                    `${asString(entry['firstName'])} ${asString(entry['lastName'])}`.trim(),
                  )
                  .join(', ') || 'None declared'
              }
            />
            <Field label="Trading name (brand)" value={asString(company['tradingName'])} path="business.company.tradingName" />
            <Field label="Legal form" value={asString(company['legalForm'])} path="business.company.legalForm" />
            <Field label="Business number / EIN" value={asString(company['businessNumber'])} path="business.company.businessNumber" />
            <Field label="Incorporation date" value={asString(company['incorporationDate'])} path="business.company.incorporationDate" />
            <Field label="TAX / VAT number" value={asString(company['taxNumber'])} path="business.company.taxNumber" />
            <Field label="Corporate website" value={asString(company['website'])} path="business.company.website" />
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Contact person
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Field
              label="Name"
              value={`${asString(company['contactName'])} ${asString(company['contactSurname'])}`.trim()}
            />
            <Field label="E-mail" value={asString(company['contactEmail'])} path="business.company.contactEmail" />
            <Field label="Telephone" value={asString(company['contactPhone'])} path="business.company.contactPhone" />
          </Box>

          {/* Both addresses were absent from this drawer entirely — a reviewer
              could not see where the company is registered or where it trades. */}
          <Typography variant="subtitle2" gutterBottom>
            Addresses
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Field
              label="Legal address"
              value={joinAddress(asRecord(business['registeredAddress']))}
            />
            <Field
              label="Actual address"
              value={
                asRecord(business['tradingAddress'])['sameAsRegistered'] === true
                  ? 'Same as legal address'
                  : joinAddress(asRecord(business['tradingAddress']))
              }
            />
          </Box>

          {/* One card per human with everything the application holds about them.
              This used to be one line per person showing a `role` key that no
              longer exists — an em dash for everyone — and an ownership figure
              read from `ownershipPercent`, which is now `shareholdingPercent`,
              so every owner displayed as 0%. */}
          {showPeople ? (
            <>
            <Typography variant="subtitle2" gutterBottom>
              People ({String(people.length)})
            </Typography>
            <Box sx={{ mb: 2 }}>
              {people.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None recorded
                </Typography>
              ) : (
                people.map((raw, index) => {
                  const person = asRecord(raw);
                  const isCompany = asString(person['entityType']) === 'COMPANY';
                  const name = isCompany
                    ? asString(person['companyName'])
                    : `${asString(person['firstName'])} ${asString(person['lastName'])}`.trim();
                  const roles = [
                    ...asStringArray(person['positions']),
                    person['isShareholder'] === true ? 'SHAREHOLDER' : '',
                    person['isUbo'] === true ? 'UBO' : '',
                  ].filter((role) => role !== '');
                  const declared = [
                    person['isUsPerson'] === true ? 'US person' : '',
                    person['isPep'] === true ? 'PEP' : '',
                    person['hasCriminalRecord'] === true ? 'Criminal record' : '',
                  ].filter((flag) => flag !== '');
                  const held = Number(
                    person['shareholdingPercent'] ?? person['ownershipPercent'] ?? 0,
                  );
                  const documentDates = [
                    asString(person['documentIssuedOn']),
                    asString(person['documentExpiresOn']),
                  ]
                    .filter((value) => value !== '')
                    .join(' — ');
                  const livingAddress = [
                    asString(person['livingAddress']),
                    asString(person['livingCity']),
                    asString(person['livingPostalCode']),
                  ]
                    .filter((value) => value !== '')
                    .join(', ');

                  return (
                    <Box
                      key={asString(person['id']) || String(index)}
                      sx={{ mb: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {name || `Person ${String(index + 1)}`}
                        {isCompany ? ' (company)' : ''}
                      </Typography>
                      <Field label="Roles" value={roles.join(', ')} />
                      {declared.length > 0 ? (
                        <Field label="Declared" value={declared.join(' · ')} />
                      ) : null}
                      <Field label="Shareholding" value={held > 0 ? `${String(held)}%` : ''} />
                      <Field
                        label="Representative rights"
                        value={asString(person['representativeRights'])}
                      />
                      {isCompany ? (
                        <Field
                          label="Registration number"
                          value={asString(person['registrationNumber'])}
                        />
                      ) : (
                        <>
                          <Field
                            label="Date of birth"
                            value={asString(person['dateOfBirth'])}
                            path={`business.people.${String(index)}.dateOfBirth`}
                          />
                          <Field
                            label="Passport number"
                            value={asString(person['passportNumber'])}
                            path={`business.people.${String(index)}.passportNumber`}
                          />
                          <Field
                            label="Identity code"
                            value={asString(person['identityCode'])}
                            path={`business.people.${String(index)}.identityCode`}
                          />
                          <Field label="Document issued / expires" value={documentDates} />
                          <Field
                            label="Citizenship"
                            value={asString(person['citizenship'])}
                            path={`business.people.${String(index)}.citizenship`}
                          />
                          <Field
                            label="Residence"
                            value={asString(person['residence'])}
                            path={`business.people.${String(index)}.residence`}
                          />
                          <Field label="Living address" value={livingAddress} />
                        </>
                      )}
                      <Field
                        label="Telephone"
                        value={asString(person['phone'])}
                        path={`business.people.${String(index)}.phone`}
                      />
                      <Field
                        label="E-mail"
                        value={asString(person['email'])}
                        path={`business.people.${String(index)}.email`}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
            </>
          ) : null}

          {/* A licence a regulated business declared was collected and never
              shown, so nobody could check it. */}
          {bizActivity['requiresLicence'] === true ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Licences
              </Typography>
              <Box sx={{ mb: 2 }}>
                {asArray(bizActivity['licences']).map((raw, index) => {
                  const licence = asRecord(raw);
                  return (
                    <Field
                      key={asString(licence['id']) || String(index)}
                      label={asString(licence['number']) || `Licence ${String(index + 1)}`}
                      value={[
                        asString(licence['type']),
                        asString(licence['location']),
                        asString(licence['status']),
                        asString(licence['dateIssued']),
                      ]
                        .filter((part) => part !== '')
                        .join(' · ')}
                    />
                  );
                })}
              </Box>
            </>
          ) : null}

          {/* Counterparties were a count. The check they exist for cannot be
              performed from a count, and a direction nobody answered looked
              exactly like one declared empty. */}
          <Typography variant="subtitle2" gutterBottom>
            Counterparties
          </Typography>
          <Box sx={{ mb: 2 }}>
            {partners.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None recorded
              </Typography>
            ) : (
              partners.map((raw, index) => {
                const partner = asRecord(raw);
                if (partner['isPlaceholder'] === true) {
                  return (
                    <Field
                      key={asString(partner['id']) || String(index)}
                      label={asString(partner['direction'])}
                      value="Declared: none yet"
                    />
                  );
                }
                return (
                  <Field
                    key={asString(partner['id']) || String(index)}
                    label={`${asString(partner['direction'])} — ${asString(partner['name'])}`}
                    value={[
                      asString(partner['country']),
                      asString(partner['category']),
                      asString(partner['website']),
                    ]
                      .filter((part) => part !== '')
                      .join(' · ')}
                  />
                );
              })
            )}
          </Box>

          {/* Who filled and signed the application — Section E's declaration,
              absent from this drawer entirely. */}
          <Typography variant="subtitle2" gutterBottom>
            Signed by
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Field
              label="Name"
              value={`${asString(bizDeclaration['signedByName'])} ${asString(
                bizDeclaration['signedBySurname'],
              )}`.trim()}
            />
            <Field label="Date" value={asString(bizDeclaration['signedDate'])} />
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Business profile
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Field label="Partners listed" value={String(partners.length)} />
            {/* Read from the sections the form actually has. These used to read
                business.kyc, a separate "profile" step that asked the same three
                questions again; removing it left both fields permanently blank
                for the reviewer. */}
            <Field
              label="Expected turnover"
              value={[
                asString(bizFinancial['incomingVolume']) !== ''
                  ? `in ${asString(bizFinancial['incomingVolume'])}`
                  : '',
                asString(bizFinancial['outgoingVolume']) !== ''
                  ? `out ${asString(bizFinancial['outgoingVolume'])}`
                  : '',
              ]
                .filter((part) => part !== '')
                .join(' · ')}
            />
            <Field
              label="Source of funds"
              value={asStringArray(bizActivity['sourceOfFunds']).join(', ')}
            />
            <Field
              label="Business description"
              value={asString(bizActivity['activityDescription'])}
              path="business.activity.activityDescription"
            />
            <Field
              label="Purpose of the account"
              value={[
                asString(bizActivity['purposeOfAccount']),
                asString(bizActivity['purposeOfAccountOther']),
              ]
                .filter((part) => part !== '')
                .join(' — ')}
            />
            <Field
              label="Key countries in / out"
              value={[
                asStringArray(bizActivity['keyCountriesIncoming']).join(', '),
                asStringArray(bizActivity['keyCountriesOutgoing']).join(', '),
              ]
                .filter((part) => part !== '')
                .join('  |  ')}
            />
        <Field label="Duration of business activity" value={asString(bizActivity['businessDuration'])} displayValue={formatBusinessDuration(asString(bizActivity['businessDuration']))} path="business.activity.businessDuration" />
            <Field label="Number of employees" value={asString(bizActivity['employeeCount'])} path="business.activity.employeeCount" />
            <Field
              label="Submits financial reports"
              value={
                bizActivity['submitsFinancialReports'] === true
                  ? asString(bizActivity['financialReportsInstitution']) || 'Yes'
                  : 'No'
              }
            />
            <Field
              label="Products"
              value={asStringArray(bizFinancial['products']).join(', ')}
            />
            <Field
              label="Planned transactions"
              value={[
                asStringArray(bizFinancial['plannedTransactions']).join(', '),
                asString(bizFinancial['plannedTransactionsOther']),
              ]
                .filter((part) => part !== '')
                .join(' · ')}
            />
            <Field
              label="Number of incoming payments"
              value={asString(bizFinancial['incomingCount'])}
              path="business.financial.incomingCount"
            />
            <Field
              label="Number of outgoing payments"
              value={asString(bizFinancial['outgoingCount'])}
              path="business.financial.outgoingCount"
            />
            <Field
              label="Maximum single transaction"
              value={asString(bizFinancial['maxSingleTransaction'])}
              path="business.financial.maxSingleTransaction"
            />
          </Box>
        </>
      ) : null}

      {showDocuments ? (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Documents ({String(allDocuments.length)})
          </Typography>
          <Box sx={{ mb: 1 }}>
            {allDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None attached
              </Typography>
            ) : (
              allDocuments.map((doc, index) => (
                <DocumentItem
                  key={`${doc.id || doc.name}-${String(index)}`}
                  applicationId={applicationId}
                  doc={doc}
                />
              ))
            )}
          </Box>
        </>
      ) : null}
    </>
  );
}
